import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../lib/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CartSheet = () => {
  const { items, isOpen, close, removeItem, updateQuantity, subtotal, totalItems } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const handleCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: email.trim() || undefined,
      };
      const { data } = await axios.post(`${API}/checkout/session`, payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Checkout failed. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Checkout failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
            data-testid="cart-overlay"
          />
          <motion.aside
            key="panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-[#0a0a0a] border-l border-neutral-800 z-[61] flex flex-col"
            data-testid="cart-panel"
          >
            <div className="flex items-center justify-between p-5 border-b border-neutral-900">
              <div className="flex items-center gap-3">
                <ShoppingBag size={16} className="text-amber-400" />
                <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400">
                  Your Cart · {totalItems}
                </div>
              </div>
              <button
                onClick={close}
                data-testid="cart-close"
                className="text-neutral-400 hover:text-white p-1"
                aria-label="Close cart"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-6 space-y-5" data-testid="cart-items">
              {items.length === 0 ? (
                <div className="text-center py-20">
                  <ShoppingBag size={32} className="text-neutral-700 mx-auto mb-5" />
                  <div className="font-display uppercase text-xl text-white mb-2">
                    Your cart is empty
                  </div>
                  <p className="text-xs text-neutral-500 uppercase tracking-[0.25em]">
                    Add a piece from the store
                  </p>
                </div>
              ) : (
                items.map((i) => (
                  <div
                    key={i.product_id}
                    data-testid={`cart-item-${i.product_id}`}
                    className="flex gap-4 border-b border-neutral-900 pb-5"
                  >
                    <div className="w-20 h-20 shrink-0 bg-[#050505] border border-neutral-900 flex items-center justify-center p-1">
                      <img
                        src={i.image}
                        alt={i.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-3">
                        <div className="font-display uppercase text-sm text-white leading-tight pr-2">
                          {i.name}
                        </div>
                        <div className="text-sm text-white shrink-0">
                          ${(i.price * i.quantity).toFixed(2)}
                        </div>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                        ${i.price.toFixed(2)} each
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-neutral-800">
                          <button
                            onClick={() => updateQuantity(i.product_id, i.quantity - 1)}
                            data-testid={`cart-qty-dec-${i.product_id}`}
                            className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-900"
                            aria-label="Decrease quantity"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="px-3 text-xs text-white min-w-[1.5rem] text-center">
                            {i.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(i.product_id, i.quantity + 1)}
                            data-testid={`cart-qty-inc-${i.product_id}`}
                            className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-900"
                            aria-label="Increase quantity"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(i.product_id)}
                          data-testid={`cart-remove-${i.product_id}`}
                          className="text-neutral-500 hover:text-amber-400 p-1"
                          aria-label="Remove item"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-neutral-900 p-5 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    Email (optional — for receipt)
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="cart-email"
                    placeholder="you@example.com"
                    className="w-full bg-transparent border-b border-neutral-800 focus:border-amber-400 text-white py-2 outline-none text-sm placeholder:text-neutral-700 mt-1"
                  />
                </div>
                <div className="flex justify-between text-sm text-neutral-300">
                  <span className="uppercase tracking-[0.25em] text-[10px] text-neutral-500">
                    Subtotal
                  </span>
                  <span className="font-display text-2xl text-white" data-testid="cart-subtotal">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <p className="text-[10px] text-neutral-500 uppercase tracking-[0.2em]">
                  Shipping & taxes calculated at checkout
                </p>
                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="cart-checkout"
                  className="w-full bg-amber-400 text-black px-5 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Redirecting
                    </>
                  ) : (
                    <>
                      Checkout <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
